import React, { useState, useEffect, useCallback } from 'react';
import { Eye } from 'lucide-react';
import { styles } from '../theme';
import { API_URL, fetchWithAuth, filterToUsersOwnBranches } from '../api';
import PosReceiptView from './PosReceiptView';
import { formatCurrency } from '../utils/formatUtils';
import { formatPHDatetime } from '../utils/dateUtils';

const formatPeso = (v) => `₱${formatCurrency(v)}`;
const formatDateTime = (v) => v ? formatPHDatetime(v) : '-';

const PosReceipts = ({ token }) => {
  const [sales, setSales] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [receiptSale, setReceiptSale] = useState(null);

  useEffect(() => {
    fetchWithAuth(`${API_URL}/branchesdropdown`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.json())
      .then(data => setBranches(filterToUsersOwnBranches(Array.isArray(data) ? data : [])))
      .catch(err => console.error('Error fetching branches:', err));
  }, [token]);

  const fetchReceipts = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: currentPage, per_page: 20, search, date_from: dateFrom, date_to: dateTo, branch_id: branchId
      });
      const res = await fetchWithAuth(`${API_URL}/pos/receipts?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setSales(data.data || []);
      setTotalCount(data.total_count || 0);
      setTotalPages(data.total_pages || 0);
    } catch (err) {
      console.error('Error fetching receipts:', err);
    }
  }, [token, currentPage, search, dateFrom, dateTo, branchId]);

  useEffect(() => { fetchReceipts(); }, [fetchReceipts]);

  const viewReceipt = async (saleId) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/pos/sales/${saleId}`, { headers: { Authorization: `Bearer ${token}` } });
      setReceiptSale(await res.json());
    } catch (err) {
      console.error('Error loading receipt:', err);
    }
  };

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.title}>Receipts</h1>
      </div>

      <div style={{ ...styles.card, marginBottom: '20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px' }}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Receipt #</label>
          <input type="text" value={search} onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }} style={styles.input} placeholder="Search..." />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Branch</label>
          <select value={branchId} onChange={(e) => { setBranchId(e.target.value); setCurrentPage(1); }} style={styles.input}>
            <option value="">All Branches</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>From</label>
          <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }} style={styles.input} />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>To</label>
          <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }} style={styles.input} />
        </div>
      </div>

      <div style={{ ...styles.card, padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Receipt #</th>
                <th style={styles.th}>Branch</th>
                <th style={styles.th}>Operator</th>
                <th style={styles.th}>Payment</th>
                <th style={styles.th}>Total</th>
                <th style={styles.th}>Status</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sales.map(s => (
                <tr key={s.id}>
                  <td style={styles.td}>{formatDateTime(s.created_at)}</td>
                  <td style={styles.td}>{s.receipt_number}</td>
                  <td style={styles.td}>{s.branch_name}</td>
                  <td style={styles.td}>{s.cashier_operator_name}</td>
                  <td style={styles.td}>{s.payment_method_name || 'Unknown'}</td>
                  <td style={styles.td}>{formatPeso(s.grand_total)}</td>
                  <td style={styles.td}>
                    <span style={{ color: s.status === 'voided' ? '#b91c1c' : '#059669' }}>{s.status}</span>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    <button onClick={() => viewReceipt(s.id)} style={{ ...styles.iconButton, color: '#4f46e5' }}>
                      <Eye size={18} />
                    </button>
                  </td>
                </tr>
              ))}
              {sales.length === 0 && (
                <tr><td colSpan={8} style={{ ...styles.td, textAlign: 'center', color: '#9ca3af' }}>No receipts found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '24px', flexWrap: 'wrap' }}>
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i + 1}
              onClick={() => setCurrentPage(i + 1)}
              style={{
                padding: '6px 12px', borderRadius: '4px', border: '1px solid #d1d5db',
                backgroundColor: currentPage === i + 1 ? '#4f46e5' : 'white',
                color: currentPage === i + 1 ? 'white' : '#374151', fontSize: '14px', cursor: 'pointer'
              }}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}
      <div style={{ textAlign: 'center', fontSize: '13px', color: '#9ca3af', marginTop: '12px' }}>
        {totalCount} receipt(s) total
      </div>

      <PosReceiptView sale={receiptSale} onClose={() => setReceiptSale(null)} />
    </div>
  );
};

export default PosReceipts;
